import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE } from "@/utils/wordGenerator/common.js";

export const createFPTemp3 = async (proposal) => {
    const style = proposal.style || {};
    const colorScheme = style.colorScheme || {};
    const headerColor = fixColor(colorScheme.header || style.box?.color || "#f0f0f0");
    const bodyColor = fixColor(colorScheme.body || style.box?.color || "#f0f0f0");
    const footerColor = fixColor(colorScheme.footer || style.box?.color || "#f0f0f0");

    // Heights
    const HEADER_HEIGHT = 1200;
    const SPACER_HEIGHT = 950;
    const PROJ_ROW_HEIGHT = 4000;
    const PROJ_HALF_HEIGHT = 3000;
    const PROJ_HALF_WIDTH = 4762;
    const PROJ_FULL_WIDTH = 7143;
    const DETAILS_HEIGHT = 4900;
    const LOGO_WIDTH = 3200;

    let logoRun = null;
    if (proposal.logo) {
        const logoData = await fetchImageWithDimensions(proposal.logo);
        if (logoData) {
            logoRun = fitImage(logoData, LOGO_WIDTH, HEADER_HEIGHT, "contain");
        }
    }

    // Fetch project images
    const projectRuns = [];
    for (const project of (proposal.projects || []).slice(0, 4)) {
        const photo = project?.uploadedPhoto || project?.photoURL;
        if (project && photo) {
            const imgData = await fetchImageWithDimensions(photo);
            if (imgData) {
                projectRuns.push(imgData);
            } else {
                projectRuns.push(null);
            }
        } else {
            projectRuns.push(null);
        }
    }

    const getPlaceholder = (width, height) => [
        new Table({
            width: { size: width, type: WidthType.DXA },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
                new TableRow({
                    height: { value: height, rule: HeightRule.EXACT },
                    children: [new TableCell({ shading: { fill: bodyColor }, children: [] })]
                })
            ]
        })
    ];

    // 1. HEADER TABLE
    const headerTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        alignment: AlignmentType.CENTER,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 70, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        children: []
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),

                    new TableCell({
                        width: { size: 27, type: WidthType.PERCENTAGE },
                        shading: { fill: headerColor },
                        verticalAlign: VerticalAlign.CENTER,
                        children: logoRun
                            ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [logoRun] })]
                            : [new Paragraph("")]
                    })
                ]
            })
        ]
    });

    // 2. PROJECTS ROW 1
    const projectsRow1Table = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        alignment: AlignmentType.CENTER,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: PROJ_ROW_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 57, type: WidthType.PERCENTAGE },
                        children: projectRuns[0]
                            ? [new Paragraph({
                                children: [fitImage(projectRuns[0], PROJ_FULL_WIDTH - 380, PROJ_HALF_HEIGHT, "cover")]
                            })]
                            : getPlaceholder(PROJ_FULL_WIDTH, PROJ_HALF_HEIGHT)
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),

                    new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        children: projectRuns[1]
                            ? [new Paragraph({
                                children: [fitImage(projectRuns[1], PROJ_HALF_WIDTH, PROJ_ROW_HEIGHT, "cover")]
                            })]
                            : getPlaceholder(PROJ_HALF_WIDTH, PROJ_ROW_HEIGHT)
                    })
                ]
            })
        ]
    });

    // 3. PROJECTS ROW 2
    const projectsRow2Table = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        alignment: AlignmentType.CENTER,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: PROJ_ROW_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        verticalAlign: VerticalAlign.BOTTOM,
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        children: projectRuns[2]
                            ? [new Paragraph({
                                children: [fitImage(projectRuns[2], PROJ_HALF_WIDTH, PROJ_ROW_HEIGHT, "cover")]
                            })]
                            : getPlaceholder(PROJ_HALF_WIDTH, PROJ_ROW_HEIGHT)
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),

                    new TableCell({
                        verticalAlign: VerticalAlign.BOTTOM,
                        width: { size: 57, type: WidthType.PERCENTAGE },
                        children: projectRuns[3]
                            ? [new Paragraph({
                                children: [fitImage(projectRuns[3], PROJ_FULL_WIDTH - 380, PROJ_HALF_HEIGHT, "cover")]
                            })]
                            : getPlaceholder(PROJ_FULL_WIDTH, PROJ_HALF_HEIGHT)
                    })
                ]
            })
        ]
    });

    // 4. DETAILS TABLE (250px blank : remaining)
    const detailsTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        alignment: AlignmentType.CENTER,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                height: { value: DETAILS_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: 30, type: WidthType.PERCENTAGE },
                        shading: { fill: footerColor },
                        children: []
                    }),
                    new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, shading: { fill: "FFFFFF" }, children: [] }),
                    new TableCell({
                        width: { size: 67, type: WidthType.PERCENTAGE },
                        shading: { fill: bodyColor },
                        verticalAlign: VerticalAlign.CENTER,
                        margins: {
                            top: 100,
                            bottom: 100,
                            left: 400,
                            right: 400
                        },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 150 },
                                children: [
                                    new TextRun({
                                        text: proposal.name || "N/A",
                                        color: fixColor(style.heading?.color),
                                        size: (style.heading?.size || 24) * SCALE,
                                        font: style.heading?.font,
                                        bold: style.heading?.bold,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline,
                                    })
                                ]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                border: {
                                    bottom: {
                                        color: "FFFFFF",
                                        space: 1,
                                        style: BorderStyle.SINGLE,
                                        size: 12,
                                    }
                                },
                                spacing: { after: 600 },
                                children: [
                                    new TextRun({
                                        text: `${proposal.address || 'N/A'}, ${proposal.city || 'N/A'}, ${proposal.stateProvince || 'N/A'}`,
                                        color: fixColor(style.heading?.color),
                                        size: (style.heading?.size || 18) * SCALE,
                                        font: style.heading?.font,
                                        bold: style.heading?.bold,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline,
                                    })
                                ]
                            }),

                            new Table({
                                width: { size: 100, type: WidthType.PERCENTAGE },
                                borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
                                rows: [
                                    new TableRow({
                                        children: [
                                            new TableCell({
                                                children: [
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "SUBMISSION DATE", color: fixColor(style.label?.color), size: (style.label?.size || 12) * SCALE, font: style.label?.font, bold: style.label?.bold, italics: style.label?.italic, underline: style.label?.underline })] }),
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: proposal.submissionDate || "N/A", color: fixColor(style.text?.color), size: (style.text?.size || 12) * SCALE, font: style.text?.font, bold: style.text?.bold, italics: style.text?.italic, underline: style.text?.underline })] })
                                                ]
                                            }),
                                            new TableCell({
                                                children: [
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "SUBMITTED BY", color: fixColor(style.label?.color), size: (style.label?.size || 12) * SCALE, font: style.label?.font, bold: style.label?.bold, italics: style.label?.italic })] }),
                                                    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: proposal.submittedBy || "N/A", color: fixColor(style.text?.color), size: (style.text?.size || 12) * SCALE, font: style.text?.font, bold: style.text?.bold, italics: style.text?.italic })] })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });

    // Main container - just stack the independent tables
    return {
        properties: {
            page: {
                margin: { top: 0, right: 0, bottom: 0, left: 0 },
            },
        },
        children: [
            headerTable,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            projectsRow1Table,
            new Paragraph({ spacing: { before: 1, after: 0 }, children: [] }),
            projectsRow2Table,
            new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
            detailsTable
        ],
    };
};
